import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { TransformControls } from 'three/addons/controls/TransformControls.js';

export function setupScene(texturePainter) {
    texturePainter.scene = new THREE.Scene();
    const canvas = document.createElement('canvas');
    canvas.width = 512;
    canvas.height = 512;
    const context = canvas.getContext('2d');
    
    const gradient = context.createLinearGradient(0, 0, 0, canvas.height);
    gradient.addColorStop(0, '#87CEEB');
    gradient.addColorStop(0.6, '#4682B4');
    gradient.addColorStop(1, '#2F4F4F');
    
    context.fillStyle = gradient;
    context.fillRect(0, 0, canvas.width, canvas.height);
    
    const backgroundTexture = new THREE.CanvasTexture(canvas);
    texturePainter.scene.background = backgroundTexture;

    texturePainter.camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.001, 2000);
    texturePainter.camera.position.set(0, 0, 2);

    const viewport = document.getElementById('viewport');
    
    let renderer;
    try {
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('webgl2') || canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
        
        if (!context) {
            throw new Error('WebGL not supported');
        }
        
        renderer = new THREE.WebGLRenderer({ 
            antialias: true, 
            preserveDrawingBuffer: true,
            powerPreference: "default",
            alpha: true,
            premultipliedAlpha: false,
            stencil: false,
            depth: true
        });
        
        renderer.setSize(100, 100);
        renderer.render(new THREE.Scene(), new THREE.Camera());
        
    } catch (error) {
        try {
            renderer = new THREE.WebGLRenderer({ 
                antialias: false, 
                preserveDrawingBuffer: false,
                powerPreference: "low-power",
                failIfMajorPerformanceCaveat: false
            });
            console.warn('WebGL2 not available, falling back to WebGL1:', error);
        } catch (fallbackError) {
            const errorDiv = document.createElement('div');
            errorDiv.innerHTML = `
                <div style="position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%); 
                            background: #f44336; color: white; padding: 20px; border-radius: 8px; 
                            text-align: center; z-index: 9999; max-width: 500px;">
                    <h3>WebGL Not Available</h3>
                    <p>This application requires WebGL support.</p>
                    <ul style="text-align: left; margin: 10px 0;">
                        <li>Enable hardware acceleration in Firefox settings</li>
                        <li>Enable WebGL in Firefox: about:config → webgl.disabled = false</li>
                        <li>Update your graphics drivers</li>
                        <li>Try a different browser (Chrome/Edge)</li>
                    </ul>
                    <button onclick="location.reload()" style="background: white; color: #f44336; border: none; padding: 8px 16px; border-radius: 4px; cursor: pointer;">
                        Retry
                    </button>
                </div>
            `;
            document.body.appendChild(errorDiv);
            throw new Error('WebGL initialization failed: ' + fallbackError.message);
        }
    }
    
    texturePainter.renderer = renderer;
    texturePainter.renderer.setSize(viewport.clientWidth, viewport.clientHeight);
    texturePainter.renderer.shadowMap.enabled = true;
    texturePainter.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    viewport.appendChild(texturePainter.renderer.domElement);

    texturePainter.controls = new OrbitControls(texturePainter.camera, texturePainter.renderer.domElement);
    texturePainter.controls.enableDamping = true;
    texturePainter.controls.dampingFactor = 0.05;

    texturePainter.transformControls = new TransformControls(texturePainter.camera, texturePainter.renderer.domElement);
    
    texturePainter.transformControls.size = 0.5;
    
    texturePainter.transformControls.addEventListener('change', () => {
        if (texturePainter.transformControls.object) {
            const scale = texturePainter.transformControls.object.scale;
            scale.x = Math.max(0.01, Math.min(100, scale.x));
            scale.y = Math.max(0.01, Math.min(100, scale.y));
            scale.z = Math.max(0.01, Math.min(100, scale.z));
        }
        texturePainter.throttledUpdate();
    });
    texturePainter.transformControls.addEventListener('dragging-changed', (event) => {
        texturePainter.controls.enabled = !event.value;
        if (!event.value) {
            texturePainter.updateProjectiveDecalPreview();
            texturePainter.render();
        }
    });
    texturePainter.scene.add(texturePainter.transformControls);

    const ambientLight = new THREE.AmbientLight(0xB0E0E6, 0.4);
    texturePainter.scene.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xFFFAF0, 0.8);
    directionalLight.position.set(10, 15, 8);
    directionalLight.castShadow = true;
    directionalLight.shadow.mapSize.width = 2048;
    directionalLight.shadow.mapSize.height = 2048;
    directionalLight.shadow.camera.near = 0.5;
    directionalLight.shadow.camera.far = 50;
    directionalLight.shadow.camera.left = -10;
    directionalLight.shadow.camera.right = 10;
    directionalLight.shadow.camera.top = 10;
    directionalLight.shadow.camera.bottom = -10;
    texturePainter.scene.add(directionalLight);

    const fillLight = new THREE.DirectionalLight(0xADD8E6, 0.3);
    fillLight.position.set(-8, 10, -5);
    texturePainter.scene.add(fillLight);

    const rimLight = new THREE.DirectionalLight(0xE6E6FA, 0.2);
    rimLight.position.set(0, 5, -10);
    texturePainter.scene.add(rimLight);

    texturePainter.animate();
}
